import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase.config';
import AdminDashboard from './AdminDashboard';
import './UserDashboard.css';

function SuperadminDashboard({ user, userData, onLogout }) {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchSuperadminData();
  }, []);

  const fetchSuperadminData = async () => {
    setLoading(true);
    
    try {
      const { data: pending, error: pendingError } = await supabase
        .from('residents')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (pendingError) throw pendingError;
      setPendingApprovals(pending || []);

      const { data: users, error: usersError } = await supabase
        .from('residents')
        .select('*')
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;
      setAllUsers(users || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to fetch users data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userIds) => {
    const idsToApprove = Array.isArray(userIds) ? userIds : [userIds];
    
    try {
      const { error } = await supabase
        .from('residents')
        .update({
          status: 'approved',
          approved_by: userData.id,
          approved_at: new Date().toISOString()
        })
        .in('id', idsToApprove);

      if (error) throw error;
      
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: userData.id,
          action: `Approved ${idsToApprove.length} user(s)`,
          entity_type: 'user',
          metadata: { approved_users: idsToApprove },
          created_at: new Date().toISOString()
        }]);
      
      alert(`${idsToApprove.length} user(s) approved successfully!`);
      setSelectedUsers([]);
      fetchSuperadminData();
    } catch (error) {
      console.error('Error approving users:', error);
      alert('Failed to approve users');
    }
  };

  const handleReject = async (userId) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;

    try {
      const { error } = await supabase
        .from('residents')
        .update({
          status: 'rejected',
          notes: reason,
          approved_by: userData.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
      
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: userData.id,
          action: 'Rejected user registration',
          entity_type: 'user',
          entity_id: userId,
          metadata: { reason: reason },
          created_at: new Date().toISOString()
        }]);
      
      alert('User rejected');
      fetchSuperadminData();
    } catch (error) {
      console.error('Error rejecting user:', error);
      alert('Failed to reject user');
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user.id);
    setEditFormData({
      name: user.name,
      flat_number: user.flat_number,
      phone: user.phone,
      role: user.role,
      account_status: user.account_status
    });
  };

  const handleEditChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSave = async (userId) => {
    setUpdateLoading(true);
    
    try {
      const originalUser = allUsers.find(u => u.id === userId);
      
      if (!originalUser) {
        throw new Error('User not found in local state');
      }

      const { data, error } = await supabase
        .from('residents')
        .update({
          name: editFormData.name,
          flat_number: editFormData.flat_number,
          phone: editFormData.phone,
          role: editFormData.role,
          account_status: editFormData.account_status,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select();

      if (error) {
        console.error('Supabase error details:', error);
        if (error.code === '42501') {
          throw new Error('Permission denied. Please check Row Level Security policies.');
        }
        throw error;
      }

      const changes = [];
      if (originalUser.name !== editFormData.name) {
        changes.push(`Name: ${originalUser.name} → ${editFormData.name}`);
      }
      if (originalUser.flat_number !== editFormData.flat_number) {
        changes.push(`Flat: ${originalUser.flat_number} → ${editFormData.flat_number}`);
      }
      if (originalUser.phone !== editFormData.phone) {
        changes.push(`Phone: ${originalUser.phone} → ${editFormData.phone}`);
      }
      if (originalUser.role !== editFormData.role) {
        changes.push(`Role: ${originalUser.role} → ${editFormData.role}`);
      }
      if (originalUser.account_status !== editFormData.account_status) {
        changes.push(`Status: ${originalUser.account_status} → ${editFormData.account_status}`);
      }
      
      if (changes.length > 0) {
        supabase
          .from('activity_logs')
          .insert([{
            user_id: userData.id,
            action: `Updated user profile: ${originalUser.email}`,
            entity_type: 'user',
            entity_id: userId,
            metadata: { 
              changes: changes,
              updated_fields: editFormData 
            },
            created_at: new Date().toISOString()
          }])
          .then(({ error }) => {
            if (error) console.error('Activity log error:', error);
          });
      }
      
      alert('User updated successfully!');
      setEditingUser(null);
      setEditFormData({});
      fetchSuperadminData();
      
    } catch (error) {
      console.error('Error updating user:', error);
      alert(`Failed to update user: ${error.message}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditingUser(null);
    setEditFormData({});
  };

  const toggleSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getRoleClass = (role) => {
    return `role-badge role-${role}`;
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status}`;
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <AdminDashboard user={user} userData={userData} onLogout={onLogout} />
      
      <div className="superadmin-section admin-main">
        <div className="dashboard-content">
          <div className="superadmin-header">
            <h2 className="section-title">
              🛡️ Superadmin Controls
              <span className="superadmin-badge">SUPER ADMIN</span>
            </h2>
          </div>
        
          {pendingApprovals.length > 0 && (
            <div className="dashboard-card">
              <div className="card-header">
                <h3>
                  ✅ Pending Approvals
                  <span className="count-badge">{pendingApprovals.length}</span>
                </h3>
              </div>
              
              <div className="card-content">
                <div className="action-bar">
                  <button
                    onClick={() => handleApprove(selectedUsers)}
                    disabled={selectedUsers.length === 0}
                    className="quick-link-btn admin-quick-btn green"
                  >
                    ✔ Approve Selected ({selectedUsers.length})
                  </button>
                  <button
                    onClick={() => setSelectedUsers(pendingApprovals.map(u => u.id))}
                    className="quick-link-btn admin-quick-btn cyan"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedUsers([])}
                    className="btn-secondary"
                  >
                    Clear Selection
                  </button>
                </div>

                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={selectedUsers.length === pendingApprovals.length && pendingApprovals.length > 0}
                            onChange={() => {
                              if (selectedUsers.length === pendingApprovals.length) {
                                setSelectedUsers([]);
                              } else {
                                setSelectedUsers(pendingApprovals.map(u => u.id));
                              }
                            }}
                            className="checkbox-input"
                          />
                        </th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Flat</th>
                        <th>Role</th>
                        <th>Registered</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingApprovals.map(user => (
                        <tr key={user.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => toggleSelection(user.id)}
                              className="checkbox-input"
                            />
                          </td>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>{user.phone}</td>
                          <td>{user.flat_number}</td>
                          <td>
                            <span className={getRoleClass(user.role)}>
                              {user.role}
                            </span>
                          </td>
                          <td>{new Date(user.created_at).toLocaleDateString()}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                onClick={() => handleApprove(user.id)}
                                className="btn-success btn-sm"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleReject(user.id)}
                                className="btn-danger btn-sm"
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="dashboard-card">
            <div className="card-header">
              <h3>
                👥 Manage All Users
                <span className="count-badge">{allUsers.length} Total</span>
              </h3>
            </div>
            
            <div className="card-content">
              {loading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading users...</p>
                </div>
              ) : allUsers.length === 0 ? (
                <div className="empty-state">
                  <p>No users found</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Flat</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Account</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map(user => (
                        <tr key={user.id}>
                          <td>
                            {editingUser === user.id ? (
                              <input
                                type="text"
                                value={editFormData.name}
                                onChange={(e) => handleEditChange('name', e.target.value)}
                                className="form-input"
                              />
                            ) : (
                              user.name
                            )}
                          </td>
                          <td>{user.email}</td>
                          <td>
                            {editingUser === user.id ? (
                              <input
                                type="tel"
                                value={editFormData.phone}
                                onChange={(e) => handleEditChange('phone', e.target.value)}
                                className="form-input"
                                maxLength="10"
                              />
                            ) : (
                              user.phone
                            )}
                          </td>
                          <td>
                            {editingUser === user.id ? (
                              <input
                                type="text"
                                value={editFormData.flat_number}
                                onChange={(e) => handleEditChange('flat_number', e.target.value)}
                                className="form-input"
                              />
                            ) : (
                              user.flat_number
                            )}
                          </td>
                          <td>
                            {editingUser === user.id ? (
                              <select
                                value={editFormData.role}
                                onChange={(e) => handleEditChange('role', e.target.value)}
                                className="form-select"
                              >
                                <option value="owner">Owner</option>
                                <option value="tenant">Tenant</option>
                                <option value="admin">Admin</option>
                                <option value="superadmin">Superadmin</option>
                              </select>
                            ) : (
                              <span className={getRoleClass(user.role)}>
                                {user.role}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={getStatusClass(user.status)}>
                              {user.status}
                            </span>
                          </td>
                          <td>
                            {editingUser === user.id ? (
                              <select
                                value={editFormData.account_status}
                                onChange={(e) => handleEditChange('account_status', e.target.value)}
                                className="form-select"
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            ) : (
                              <span className={getStatusClass(user.account_status)}>
                                {user.account_status}
                              </span>
                            )}
                          </td>
                          <td>
                            {editingUser === user.id ? (
                              <div className="action-buttons">
                                <button
                                  onClick={() => handleEditSave(user.id)}
                                  disabled={updateLoading}
                                  className="btn-success btn-sm"
                                >
                                  {updateLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={handleEditCancel}
                                  disabled={updateLoading}
                                  className="btn-secondary btn-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleEditClick(user)}
                                className="btn-primary btn-sm"
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SuperadminDashboard;